import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { TextField, Box, Modal } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Editor } from '@tiptap/react';
import { Button } from '../../../ui/button';
import { MAX_URL_LENGTH } from '../../../../utils/markdownUtils';
import {
  isSupportedLinkUrl,
  normaliseLinkUrl
} from '../../../../utils/linkUrlUtils';
import styles from '../styles';

const UNSUPPORTED_LINK_MESSAGE =
  'Enter a web address (https://...) or an email address';
const LINK_PLACEHOLDER = 'Web address or email';

interface FormValues {
  link: string;
}

interface LinkModalProps {
  editor: Editor;
  showLinkModal: boolean;
  setShowLinkModal: React.Dispatch<React.SetStateAction<boolean>>;
}

function LinkModal({
  editor,
  showLinkModal,
  setShowLinkModal
}: Readonly<LinkModalProps>): React.ReactElement {
  const previousUrl = editor.getAttributes('link').href;

  const inputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef]);

  const handleClose = (): void => setShowLinkModal(false);

  const validationSchema = Yup.object()
    .shape({
      link: Yup.string()
        .transform(normaliseLinkUrl)
        .required('Link is required')
        .test('supported-link', UNSUPPORTED_LINK_MESSAGE, (value) =>
          isSupportedLinkUrl(value ?? '')
        )
        .max(
          MAX_URL_LENGTH,
          `URL is too long (maximum ${MAX_URL_LENGTH} characters)`
        )
    })
    .required();

  const {
    handleSubmit,
    register,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema)
  });

  const onSubmit = (data: FormValues): void => {
    setShowLinkModal(false);
    const { link } = data;

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: link })
      .run();
  };

  return (
    <Modal
      open={showLinkModal}
      disablePortal
      onClose={handleClose}
      sx={styles.linkModalStyles}
    >
      <Box
        component='form'
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return undefined;
          e.preventDefault();
          e.stopPropagation();
          return handleSubmit(onSubmit)();
        }}
        noValidate
      >
        <TextField
          {...register('link')}
          id='link'
          label='Link'
          inputRef={inputRef}
          defaultValue={previousUrl || ''}
          error={Boolean(errors.link)}
          helperText={errors.link?.message}
          placeholder={LINK_PLACEHOLDER}
          margin='none'
          fullWidth
          sx={{
            '& input': {
              background: '#FFF'
            },
            mb: 1
          }}
        />
        <Box sx={styles.linkModalButtonStyles}>
          <Button type='button' variant='outline' onClick={handleClose}>
            Cancel
          </Button>
          <Button type='button' onClick={() => handleSubmit(onSubmit)()}>
            Add link
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default LinkModal;
